import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Table, Button, Tooltip, Space, Modal, message, Tag } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, WarningOutlined } from '@ant-design/icons';
import { AppDispatch, RootState } from '../../redux/store/store';
import { deleteUsuario, fetchUsuarios, setUsuariosLimit, setUsuariosPage } from '../../redux/actions/usuarios/usuarios';
import EditarUsuarioModal from '../modals/usuarios/EditUserModal';
import CrearUsuarioModal from '../modals/usuarios/CreateUserModal';

interface ColumnItem {
  key: string;
  title: string;
  dataIndex: string;
  width?: number;
  ellipsis?: boolean;
  render?: any;
}

const ListaUsuarios = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { usuarios, loading, pagination } = useSelector((state: RootState) => state.usuarios);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [selectedUsuarioId, setSelectedUsuarioId] = useState<number | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    dispatch(fetchUsuarios(pagination.page, pagination.limit));
  }, [dispatch, pagination.page, pagination.limit]);

  const handleTableChange = (pagination: any) => {
    dispatch(setUsuariosPage(pagination.current));
    dispatch(fetchUsuarios(pagination.current, pagination.pageSize));
  };

  const handlePageSizeChange = (current: number, size: number) => {
    dispatch(setUsuariosLimit(size));
    dispatch(fetchUsuarios(current, size));
  };

  const columns: ColumnItem[] = [
    {
      key: 'item',
      title: 'Item',
      dataIndex: 'id',
      width: 55,
      render: (_: any, __: any, index: number) => (pagination.page - 1) * pagination.limit + index + 1
    },
    {
      key: 'usuario',
      title: 'Usuario',
      dataIndex: 'usuario',
      width: 100,
      ellipsis: true,
    },
    {
      key: 'nombre',
      title: 'Nombre Completo',
      dataIndex: 'personas',
      width: 150,
      ellipsis: true,
      render: (personas: any) => (
        <Tooltip title={personas ? `${personas.nombre} ${personas.apellidos}` : 'Sin datos'}>
          <span>{personas ? `${personas.nombre} ${personas.apellidos}` : 'Sin datos'}</span>
        </Tooltip>
      ),
    },
    {
      key: 'email',
      title: 'Email',
      dataIndex: 'email',
      width: 150,
      ellipsis: true,
      render: (email: string) => (
        <Tooltip title={email}>
          <span>{email}</span>
        </Tooltip>
      ),
    },
    {
      key: 'empresa',
      title: 'Empresa',
      dataIndex: 'empresa',
      width: 120,
      ellipsis: true,
      render: (empresa: any) => (
        <Tooltip title={empresa?.nombre || 'Sin empresa'}>
          <span>{empresa?.nombre || 'Sin empresa'}</span>
        </Tooltip>
      ),
    },
    {
      key: 'sucursales',
      title: 'Sucursales',
      dataIndex: 'sucursales',
      width: 120,
      ellipsis: true,
      render: (sucursales: any[]) => {
        if (sucursales && sucursales.length > 0) {
          const tooltipText = sucursales.map(s => s.nombre).join(', ');
          return (
            <Tooltip title={tooltipText}>
              <span>{sucursales.length > 1 ? `${sucursales[0].nombre} +${sucursales.length - 1}` : sucursales[0].nombre}</span>
            </Tooltip>
          );
        }

        return (
          <Tooltip title="El usuario no tiene sucursales asignadas">
            <Tag icon={<WarningOutlined />} color="warning">Sin sucursales</Tag>
          </Tooltip>
        );
      },
    },
    {
      key: 'tipo_usuario',
      title: 'Tipo de Usuario',
      dataIndex: 'tipos_usuarios',
      width: 100,
      ellipsis: true,
      render: (tipoUsuario: any) => (
        <Tooltip title={tipoUsuario ? tipoUsuario.tipo_usuario : 'Sin tipo asignado'}>
          {tipoUsuario ? (
            <span>{tipoUsuario.tipo_usuario}</span>
          ) : (
            <Tag color="default">Sin tipo</Tag>
          )}
        </Tooltip>
      ),
    },
    {
      key: 'estado',
      title: 'Estado',
      dataIndex: 'estado',
      width: 90,
      render: (estado: boolean) => (
        <Tag
          color={estado ? 'success' : 'error'}
          className="text-center font-bold px-2 py-1 w-20"
        >
          {estado ? 'Activo' : 'Inactivo'}
        </Tag>
      ),
    },
    {
      key: 'fecha_creacion',
      title: 'Fecha de Creación',
      dataIndex: 'fecha_creacion',
      width: 100,
      ellipsis: true,
    },
    {
      key: 'acciones',
      title: 'Acciones',
      dataIndex: 'id',
      width: 100,
      render: (id: number) => (
        <Space size="middle">
          <Tooltip title="Editar">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEditUsuario(id)}
            />
          </Tooltip>
          <Tooltip title="Eliminar">
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              onClick={() => showDeleteConfirm(id)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const handleEditUsuario = (id: number) => {
    setSelectedUsuarioId(id);
    setEditModalVisible(true);
  };

  const handleEditModalClose = (refresh: boolean = false) => {
    setEditModalVisible(false);
    setSelectedUsuarioId(null);
    if (refresh) {
      dispatch(fetchUsuarios(pagination.page, pagination.limit));
    }
  };

  const showDeleteConfirm = (id: number) => {
    setSelectedUsuarioId(id);
    setDeleteModalVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (selectedUsuarioId) {
      setDeleteLoading(true);
      try {
        await dispatch(deleteUsuario(selectedUsuarioId.toString()));
        setDeleteModalVisible(false);
        dispatch(fetchUsuarios(pagination.page, pagination.limit));
        message.success('Usuario eliminado exitosamente');
      } catch (error) {
        console.error('Error al eliminar el usuario:', error);
      } finally {
        setDeleteLoading(false);
      }
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalVisible(false);
    setSelectedUsuarioId(null);
  };

  const handleCreateUsuario = () => {
    setCreateModalVisible(true);
  };

  const handleCreateModalClose = (refresh: boolean = false) => {
    setCreateModalVisible(false);
    if (refresh) {
      dispatch(fetchUsuarios(pagination.page, pagination.limit));
    }
  };

  return (
    <div className="py-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Lista de Usuarios</h1>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreateUsuario}
        >
          Nuevo Usuario
        </Button>
      </div>

      <div className="overflow-x-auto">
        <Table
          columns={columns}
          dataSource={usuarios}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1300 }}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total) => `Total ${total} usuarios`,
            onChange: (page, pageSize) => handleTableChange({ current: page, pageSize }),
            onShowSizeChange: handlePageSizeChange
          }}
        />
      </div>

      <Modal
        title="Confirmar eliminación"
        open={deleteModalVisible}
        onOk={handleDeleteConfirm}
        onCancel={handleCancelDelete}
        confirmLoading={deleteLoading}
        okText="Eliminar"
        cancelText="Cancelar"
        okButtonProps={{ danger: true }}
      >
        <p>¿Está seguro que desea eliminar este usuario? Esta acción no se puede deshacer.</p>
      </Modal>

      {editModalVisible && (
        <EditarUsuarioModal
          visible={editModalVisible}
          usuarioId={selectedUsuarioId}
          onClose={handleEditModalClose}
        />
      )}

      {createModalVisible && (
        <CrearUsuarioModal
          visible={createModalVisible}
          onClose={handleCreateModalClose}
        />
      )}
    </div>
  );
};

export default ListaUsuarios;